'use client';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

export interface FormFieldProps {
  label: string;
  description?: string;
  required?: boolean;
  error?: string;
  className?: string;
}

export interface TextFieldProps extends FormFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?:
    | 'text'
    | 'email'
    | 'tel'
    | 'url'
    | 'number'
    | 'date'
    | 'datetime-local';
  min?: string | number;
  max?: string | number;
  step?: string | number;
  /**
   * **Geometry overrides for a host that is not a page form** (VALE-863).
   *
   * This field's own `space-y-2` + default `Label`/`Input` heights are page-form
   * measurements, and they are right for a page form. The attribute treatment's
   * composite-value popovers are not one: their parts are a 12px label over a
   * 13px control, and five of these at page-form height is what made the
   * address popover read taller and looser than the popovers beside it.
   *
   * The classes are **passed in rather than named here on purpose.** They belong
   * to the attribute treatment's token module, and `components/ui` is the layer
   * below it — a `density: 'compact'` arm here would either restate those values
   * (the second definition the root `CLAUDE.md` forbids) or import upward. So
   * the caller that already owns the tokens hands them down; see
   * `ATTRIBUTE_POPOVER_PART*` in `components/attributes/attribute-tokens.ts`.
   *
   * Every existing call site omits these and renders byte-for-byte as before.
   */
  labelClassName?: string;
  controlClassName?: string;
}

export interface TextAreaFieldProps extends FormFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export interface SelectFieldProps extends FormFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}

export interface FileFieldProps extends FormFieldProps {
  id: string;
  onChange: (file: File | null) => void;
  accept?: string;
  multiple?: boolean;
}

export interface CheckboxFieldProps extends FormFieldProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface AddressFieldGroupProps {
  value: {
    streetAddress: string;
    suburb: string;
    state: string;
    postcode: string;
    country: string;
  };
  onChange: (field: string, value: string) => void;
  required?: boolean;
}

export function TextField({
  id,
  label,
  description,
  required,
  error,
  value,
  onChange,
  placeholder,
  type = 'text',
  className,
  min,
  max,
  step,
  labelClassName,
  controlClassName
}: TextFieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id} className={labelClassName}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        min={min}
        max={max}
        step={step}
        className={cn(controlClassName, error && 'border-destructive')}
      />
      {description && (
        <p className="text-muted-foreground text-xs">{description}</p>
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}

export function TextAreaField({
  id,
  label,
  description,
  required,
  error,
  value,
  onChange,
  placeholder,
  rows = 3,
  className
}: TextAreaFieldProps) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={rows}
        className={error ? 'border-destructive' : ''}
      />
      {description && (
        <p className="text-muted-foreground text-xs">{description}</p>
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}

export function SelectField({
  label,
  description,
  required,
  error,
  value,
  onChange,
  placeholder,
  options,
  className
}: SelectFieldProps) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={error ? 'border-destructive' : ''}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && (
        <p className="text-muted-foreground text-xs">{description}</p>
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}

export function CheckboxField({
  id,
  label,
  description,
  required,
  error,
  checked,
  onChange,
  className
}: CheckboxFieldProps) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      <div className="flex items-center space-x-2">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={onChange}
          required={required}
          className={error ? 'border-destructive' : ''}
        />
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>
      {description && (
        <p className="text-muted-foreground ml-6 text-xs">{description}</p>
      )}
      {error && <p className="text-destructive ml-6 text-xs">{error}</p>}
    </div>
  );
}

export function FileField({
  id,
  label,
  description,
  required,
  error,
  onChange,
  accept,
  multiple,
  className
}: FileFieldProps) {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onChange(files[0]);
    } else {
      onChange(null);
    }
  };

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Input
        id={id}
        type="file"
        onChange={handleFileChange}
        accept={accept}
        multiple={multiple}
        required={required}
        className={`cursor-pointer ${error ? 'border-destructive' : ''}`}
      />
      {description && (
        <p className="text-muted-foreground text-xs">{description}</p>
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}

export function AddressFieldGroup({
  value,
  onChange,
  required
}: AddressFieldGroupProps) {
  return (
    <div className="space-y-4">
      <TextField
        id="streetAddress"
        label="Street Address"
        value={value.streetAddress}
        onChange={(val) => onChange('streetAddress', val)}
        required={required}
      />
      <div className="grid grid-cols-3 gap-4">
        <TextField
          id="suburb"
          label="Suburb"
          value={value.suburb}
          onChange={(val) => onChange('suburb', val)}
          required={required}
        />
        <TextField
          id="state"
          label="State"
          value={value.state}
          onChange={(val) => onChange('state', val)}
          required={required}
        />
        <TextField
          id="postcode"
          label="Postcode"
          value={value.postcode}
          onChange={(val) => onChange('postcode', val)}
          required={required}
        />
      </div>
      <TextField
        id="country"
        label="Country"
        value={value.country}
        onChange={(val) => onChange('country', val)}
        placeholder="Australia"
      />
    </div>
  );
}
